import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { 
  Video, Radio, Calendar, Clock, Eye, Users, Play, 
  Plus, ArrowLeft, Trash2, Edit, 
  ChevronRight, Settings, Share2
} from "lucide-react";
import { toast } from "sonner";
import { CreateStreamDialog } from "@/components/BidZuuLive";
import api from "@/utils/api";

// Theme display mapping
const THEME_LABELS = {
  'fashion_night': 'Fashion Night',
  'designer_deals': 'Designer Deals',
  'electronics_showcase': 'Electronics Showcase',
  'vintage_finds': 'Vintage Finds',
  'sneaker_drop': 'Sneaker Drop',
  'jewelry_gems': 'Jewelry & Gems',
  'home_decor': 'Home Decor',
  'collectibles': 'Collectibles',
  'sports_memorabilia': 'Sports Memorabilia',
  'luxury_resale': 'Luxury Resale',
  'clearance_sale': 'Clearance Sale',
  'mystery_boxes': 'Mystery Boxes',
};

// Show Card Component
const ShowCard = ({ show, onDelete, onEdit, onGoLive }) => {
  const navigate = useNavigate();
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'live': return 'bg-red-500 text-white animate-pulse';
      case 'scheduled': return 'bg-blue-500 text-white';
      case 'ended': return 'bg-slate-500 text-white';
      case 'created': return 'bg-emerald-500 text-white';
      default: return 'bg-slate-500 text-white';
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not scheduled';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/live/${show.stream_id}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: show.title,
          text: `Watch my live show: ${show.title}`,
          url: shareUrl
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
      }
    } catch (err) {
      // User cancelled share
    }
  };

  return (
    <Card className="overflow-hidden bg-slate-800/50 border-slate-700 hover:border-slate-600 transition group" data-testid={`show-card-${show.stream_id}`}>
      {/* Thumbnail */}
      <div className="relative aspect-video bg-slate-900">
        {show.thumbnail ? (
          <img 
            src={show.thumbnail} 
            alt={show.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900">
            <Video className="h-12 w-12 text-slate-700" />
          </div>
        )}
        
        {/* Status Badge */}
        <Badge className={`absolute top-3 left-3 ${getStatusColor(show.status)}`}>
          {show.status === 'live' && <Radio className="h-3 w-3 mr-1 animate-pulse" />}
          {show.status?.toUpperCase()}
        </Badge>
        
        {/* Product Count */}
        {show.products?.length > 0 && (
          <Badge className="absolute top-3 right-3 bg-black/70 text-white">
            {show.products.length} Items
          </Badge>
        )}
        
        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-3">
          {show.status === 'created' || show.status === 'scheduled' ? (
            <Button 
              size="sm" 
              className="bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700"
              onClick={() => onGoLive(show)}
            >
              <Play className="h-4 w-4 mr-1" /> Go Live
            </Button>
          ) : show.status === 'live' ? (
            <Button 
              size="sm" 
              className="bg-gradient-to-r from-red-500 to-rose-600"
              onClick={() => navigate(`/live/${show.stream_id}`)}
            >
              <Eye className="h-4 w-4 mr-1" /> View Stream
            </Button>
          ) : (
            <Button 
              size="sm" 
              variant="outline"
              className="bg-white/10 border-white/30 text-white"
              onClick={() => navigate(`/live/${show.stream_id}`)}
            >
              <Eye className="h-4 w-4 mr-1" /> View Details
            </Button>
          )}
        </div>
      </div>
      
      {/* Content */}
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-white truncate">{show.title}</h3>
            {show.theme && (
              <Badge variant="outline" className="mt-1 border-slate-600 text-slate-300 text-xs">
                {THEME_LABELS[show.theme] || show.theme}
              </Badge>
            )}
          </div>
        </div>
        
        {/* Stats Row */}
        <div className="flex items-center gap-4 mt-3 text-sm text-slate-400">
          <div className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            {formatDate(show.scheduled_at || show.created_at)}
          </div>
          {show.status === 'live' && (
            <div className="flex items-center gap-1 text-red-400">
              <Eye className="h-4 w-4" />
              {show.viewer_count || 0} watching
            </div>
          )}
          {show.status === 'ended' && show.peak_viewers > 0 && (
            <div className="flex items-center gap-1">
              <Users className="h-4 w-4" />
              Peak: {show.peak_viewers}
            </div>
          )}
        </div>
        
        {/* Feature badges */}
        <div className="flex gap-2 mt-3">
          {show.low_starts && (
            <Badge className="bg-emerald-500/20 text-emerald-400 text-xs border-0">$5 Low Starts</Badge>
          )}
          {show.accept_shared && (
            <Badge className="bg-blue-500/20 text-blue-400 text-xs border-0">Community</Badge>
          )}
        </div>
        
        {/* Action Buttons - Always Visible */}
        <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-700">
          <Button
            size="sm"
            variant="outline"
            className="flex-1 bg-slate-700/50 border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
            onClick={() => onEdit(show)}
          >
            <Edit className="h-4 w-4 mr-1" /> Edit
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="bg-slate-700/50 border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
            onClick={handleShare}
          >
            <Share2 className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20 hover:text-red-300"
            onClick={() => onDelete(show)}
            data-testid={`delete-show-${show.stream_id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Main My Shows Page
const MyShowsPage = ({ user }) => {
  const navigate = useNavigate();
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, scheduled, live, ended
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [userListings, setUserListings] = useState([]);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    fetchShows();
    fetchListings();
  }, [user]);

  const fetchShows = async () => {
    try {
      setLoading(true);
      const response = await api.get('/live/my-shows');
      setShows(response.data.shows || []);
    } catch (error) {
      console.error('Error fetching shows:', error);
      toast.error('Failed to load your shows');
    } finally {
      setLoading(false);
    }
  };

  const fetchListings = async () => {
    try {
      const response = await api.get('/listings/my');
      setUserListings(response.data.listings || []);
    } catch (error) {
      console.error('Error fetching listings:', error);
    }
  };

  const handleDelete = async (show) => {
    if (!confirm(`Are you sure you want to delete "${show.title}"?`)) return;
    
    try {
      await api.delete(`/live/streams/${show.stream_id}`);
      toast.success('Show deleted');
      fetchShows();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete show');
    }
  };

  const handleEdit = (show) => {
    // For now, we'll just show a toast - could open an edit dialog
    toast.info('Edit functionality coming soon!');
  };

  const handleGoLive = (show) => {
    navigate(`/live/${show.stream_id}`);
  };

  const handleShowCreated = (stream) => {
    setShowCreateDialog(false);
    fetchShows();
    if (!stream.scheduled_at) {
      navigate(`/live/${stream.stream_id}`);
    } else {
      toast.success('Show scheduled successfully!');
    }
  };

  const filteredShows = filter === 'all' 
    ? shows 
    : shows.filter(s => s.status === filter);

  const scheduledCount = shows.filter(s => s.status === 'scheduled').length;
  const liveCount = shows.filter(s => s.status === 'live').length;
  const endedCount = shows.filter(s => s.status === 'ended').length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800" data-testid="my-shows-page">
      {/* Header */}
      <div className="bg-slate-900/80 backdrop-blur border-b border-slate-700 sticky top-0 z-40">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/live" className="text-slate-400 hover:text-white transition">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-lg flex items-center justify-center">
                  <Radio className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-white">My Shows</h1>
                  <p className="text-xs text-slate-400">Manage your live auctions</p>
                </div>
              </div>
            </div>
            
            <Button 
              onClick={() => setShowCreateDialog(true)}
              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
              data-testid="create-show-btn"
            >
              <Plus className="h-5 w-5 mr-2" />
              New Show
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8">
          <button
            onClick={() => setFilter('all')}
            className={`p-3 md:p-4 rounded-xl text-left transition ${
              filter === 'all' 
                ? 'bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border-2 border-emerald-500/50' 
                : 'bg-slate-800/50 border border-slate-700 hover:border-slate-600'
            }`}
          >
            <p className="text-2xl md:text-3xl font-bold text-white">{shows.length}</p>
            <p className="text-xs md:text-sm text-slate-400">Total Shows</p>
          </button>
          <button
            onClick={() => setFilter('scheduled')}
            className={`p-3 md:p-4 rounded-xl text-left transition ${
              filter === 'scheduled' 
                ? 'bg-gradient-to-br from-blue-500/20 to-indigo-500/20 border-2 border-blue-500/50' 
                : 'bg-slate-800/50 border border-slate-700 hover:border-slate-600'
            }`}
          >
            <p className="text-2xl md:text-3xl font-bold text-blue-400">{scheduledCount}</p>
            <p className="text-xs md:text-sm text-slate-400">Scheduled</p>
          </button>
          <button
            onClick={() => setFilter('live')}
            className={`p-3 md:p-4 rounded-xl text-left transition ${
              filter === 'live' 
                ? 'bg-gradient-to-br from-red-500/20 to-rose-500/20 border-2 border-red-500/50' 
                : 'bg-slate-800/50 border border-slate-700 hover:border-slate-600'
            }`}
          >
            <p className="text-2xl md:text-3xl font-bold text-red-400">{liveCount}</p>
            <p className="text-xs md:text-sm text-slate-400">Live Now</p>
          </button>
          <button
            onClick={() => setFilter('ended')}
            className={`p-3 md:p-4 rounded-xl text-left transition ${
              filter === 'ended' 
                ? 'bg-gradient-to-br from-slate-500/20 to-gray-500/20 border-2 border-slate-500/50' 
                : 'bg-slate-800/50 border border-slate-700 hover:border-slate-600'
            }`}
          >
            <p className="text-2xl md:text-3xl font-bold text-slate-400">{endedCount}</p>
            <p className="text-xs md:text-sm text-slate-400">Completed</p>
          </button>
        </div>

        {/* Shows Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin h-10 w-10 border-4 border-emerald-500 border-t-transparent rounded-full" />
          </div>
        ) : filteredShows.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-24 h-24 mx-auto mb-6 bg-slate-800 rounded-2xl flex items-center justify-center">
              <Video className="h-12 w-12 text-slate-600" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">
              {filter === 'all' ? 'No Shows Yet' : `No ${filter} shows`}
            </h2>
            <p className="text-slate-400 mb-6">Create your first live show and start selling!</p>
            <Button 
              onClick={() => setShowCreateDialog(true)}
              className="bg-gradient-to-r from-emerald-500 to-teal-600"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create Your First Show
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredShows.map(show => (
              <ShowCard
                key={show.stream_id}
                show={show}
                onDelete={handleDelete}
                onEdit={handleEdit}
                onGoLive={handleGoLive}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create Stream Dialog */}
      <CreateStreamDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onCreated={handleShowCreated}
        userListings={userListings}
      />
    </div>
  );
};

export default MyShowsPage;
